<script setup>
import { ref, computed, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api } from '@/api/api.js'
import { getAuthorBrandOverride } from '@/config/authorBranding'

const route = useRoute()

const defaultEmail = 'chapter.f4@gmail.com'
const defaultPhone = '+7 (981) 113-15-60'
const defaultTelHref = '+79811131560'

const personalContacts = ref(null)

// Контакты автора показываем и на его странице, и в его КП.
const isAuthorContext = computed(() => route.name === 'author' || route.name === 'author-kp')
const isDevelopPage = computed(() => route.path === '/develop')

function normalizeTel(phone) {
  if (!phone) return ''
  return String(phone).replace(/[^\d+]/g, '')
}

watch(
  () => [route.name, route.params?.name],
  async ([routeName, userName]) => {
    if ((routeName !== 'author' && routeName !== 'author-kp') || !userName) {
      personalContacts.value = null
      return
    }
    try {
      const author = await api.getAuthor(userName)
      personalContacts.value = author?.contacts ?? author ?? null
    } catch (_e) {
      personalContacts.value = null
    }
  },
  { immediate: true },
)

const footerEmail = computed(() => {
  if (!isAuthorContext.value) return defaultEmail
  return personalContacts.value?.email || defaultEmail
})

const footerPhone = computed(() => {
  if (!isAuthorContext.value) return defaultPhone
  return personalContacts.value?.phone || personalContacts.value?.phone_number || defaultPhone
})

const footerMailtoHref = computed(() => `mailto:${footerEmail.value}`)
const footerTelHref = computed(() => {
  const tel = normalizeTel(footerPhone.value)
  return `tel:${tel || defaultTelHref}`
})
const footerEmailLabel = computed(() => String(footerEmail.value || '').toUpperCase())

// Временная кастомизация: на страницах отдельных авторов заголовок подвала заменяется
// на их бренд и ведёт на страницу автора.
const authorBrand = computed(() => getAuthorBrandOverride(route.name, route.params))
const footerTitle = computed(() => authorBrand.value?.text || 'CHAPTER FOUR PRODUCTION')
const footerTo = computed(() => authorBrand.value?.to || '/')
</script>

<template>
  <footer :class="{ 'footer--no-top-gap': isDevelopPage }">
    <div class="footer-container">
      <RouterLink :to="footerTo" class="footer-title">{{ footerTitle }}</RouterLink>
      <div class="footer-content">
        <div class="footer-left">
          <a :href="footerMailtoHref" class="footer-item">{{ footerEmailLabel }}</a>
          <a :href="footerTelHref" class="footer-item">{{ footerPhone }}</a>
        </div>
        <div class="footer-right">
          <RouterLink to="/policy" class="footer-link">ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ</RouterLink>
          <RouterLink to="/terms" class="footer-link">ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ</RouterLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
footer {
  display: flex;
  justify-content: center;
  background-color: #1a1a1a;
  width: 100dvw;
  margin-top: 2rem;
  padding: 1.5rem 0;
  transition: background-color 0.3s ease;
}

.footer--no-top-gap {
  margin-top: 0;
}

.footer-container {
  position: relative;
  max-width: 1524px;
  width: 100%;
  margin: 0 auto;
  padding: 0 4rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
}

.footer-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
}

.footer-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.375rem;
}

.footer-title {
  display: block;
  width: 100%;
  color: #fff;
  font-family: Montserrat, Arial, sans-serif;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.64px;
  text-transform: uppercase;
  text-decoration: none;
}

.footer-title:hover {
  opacity: 0.9;
}

.footer-item,
.footer-link {
  position: relative;
  font-family: Montserrat, Arial, sans-serif;
  font-size: 0.813rem;
  font-weight: 400;
  text-transform: uppercase;
  color: #fff;
  text-decoration: none;
  margin-bottom: 0.1rem;
}

.footer-item::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 0.063rem;
  bottom: -0.25rem;
  left: 50%;
  background-color: #fff;
  transition: all 0.3s ease;
  transform: translateX(-50%) scaleX(0);
}

.footer-item:hover::after {
  transform: translateX(-50%) scaleX(1);
}

.footer-link {
  transition: transform 0.3s ease;
}

.footer-link:hover {
  transform: translateY(-2px);
}

@media (max-width: 64rem) {
  .footer-container {
    padding: 0 2.5rem;
  }
}

@media (max-width: 52.5rem) {
  .footer-container {
    padding: 0 2.25rem;
  }
}

@media (max-width: 48rem) {
  footer {
    padding: 2rem 0;
  }

  .footer-container {
    padding: 0 1.25rem;
  }

  .footer-title {
    text-align: center;
  }

  .footer-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: 0 auto;
    gap: 1.25rem;
  }

  .footer-left {
    align-items: center;
    gap: 0.5rem;
  }

  .footer-right {
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
